import type { ApiErrorResponse } from '@aegis/common'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly response: ApiErrorResponse,
  ) {
    super(response.message)
    this.name = 'ApiError'
  }
}

// Mutex: only one refresh at a time. Concurrent 401s share the same refresh promise.
let refreshPromise: Promise<boolean> | null = null

/**
 * Endpoints that don't require an Authorization header AND should never
 * trigger the silent-refresh-on-401 flow. Includes the passwordless
 * primitives: register, refresh, logout, plus magic-link request/verify/
 * consume and PAT exchange. Hitting these without a JWT must NOT cause
 * the client to attempt /auth/refresh and clear the auth store.
 */
const PUBLIC_AUTH_PATHS = new Set([
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/login-link/request',
  '/auth/login-link/verify',
  '/auth/login-link/consume',
  '/auth/access-token/exchange',
])

function normalizePath(path: string): string {
  return path.split('?')[0] ?? path
}

async function doRefresh(): Promise<boolean> {
  const { useAuthStore } = await import('./auth')
  try {
    await useAuthStore.getState().refresh()
    return !!useAuthStore.getState().accessToken
  } catch {
    return false
  }
}

function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

/**
 * Unwraps the standard backend envelope: { data: T, meta?: ... }
 * Returns data directly.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { useAuthStore } = await import('./auth')
  const { accessToken } = useAuthStore.getState()
  const normalizedPath = normalizePath(path)
  const isPublicAuthPath = PUBLIC_AUTH_PATHS.has(normalizedPath)

  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (accessToken && !isPublicAuthPath) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  const baseUrl = import.meta.env.VITE_API_URL || '/api'
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({
      statusCode: response.status,
      code: 'UNKNOWN_ERROR',
      message: response.statusText,
      traceId: '',
      timestamp: new Date().toISOString(),
    }))) as ApiErrorResponse

    // Subscription required → redirect to billing
    const bodyCode = (body as { code?: string }).code
    if (response.status === 403 && bodyCode === 'SUBSCRIPTION_REQUIRED') {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/billing')) {
        window.location.href = '/billing'
      }
      throw new ApiError(response.status, body)
    }

    // 401: attempt silent refresh, then retry once
    // 403 from Lambda Authorizer (no structured body/code) = expired JWT → also refresh
    // 403 from backend (has code: "FORBIDDEN") = permission denied → do NOT refresh, just throw
    const isAuthExpiry = response.status === 401 ||
      (response.status === 403 && bodyCode !== 'FORBIDDEN' && bodyCode !== 'SUBSCRIPTION_REQUIRED')
    if (isAuthExpiry && !isPublicAuthPath) {
      const refreshed = await refreshOnce()
      if (refreshed) {
        const { accessToken: newToken } = useAuthStore.getState()
        if (newToken) {
          const retryHeaders = new Headers(options.headers)
          if (!retryHeaders.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
            retryHeaders.set('Content-Type', 'application/json')
          }
          if (!isPublicAuthPath) {
            retryHeaders.set('Authorization', `Bearer ${newToken}`)
          }
          const retry = await fetch(`${baseUrl}${path}`, { ...options, headers: retryHeaders, credentials: 'include' })
          if (retry.ok) {
            if (retry.status === 204) return undefined as T
            const retryJson = await retry.json()
            if (retryJson && typeof retryJson === 'object' && 'data' in retryJson) return retryJson.data as T
            return retryJson as T
          }

          const retryBody = (await retry.json().catch(() => ({
            statusCode: retry.status,
            code: 'UNKNOWN_ERROR',
            message: retry.statusText,
            traceId: '',
            timestamp: new Date().toISOString(),
          }))) as ApiErrorResponse
          const retryCode = (retryBody as { code?: string }).code
          // After a successful refresh, a 403 is usually a real authorization/business error.
          // Treat it as auth-expiry only when the code is missing/unknown-auth.
          const retryIsAuthExpiry = retry.status === 401 || (
            retry.status === 403 && (
              !retryCode ||
              retryCode === 'UNAUTHORIZED' ||
              retryCode === 'TOKEN_EXPIRED' ||
              retryCode === 'UNKNOWN_ERROR'
            )
          )
          if (!retryIsAuthExpiry) {
            throw new ApiError(retry.status, retryBody)
          }
        }
      }
      useAuthStore.getState().clearAuth()
      window.location.href = '/login'
    }

    throw new ApiError(response.status, body)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const json = await response.json()
  // Unwrap { data: T } envelope if present
  if (json && typeof json === 'object' && 'data' in json) {
    return json.data as T
  }
  return json as T
}
